import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug, AlertCircle, Wifi } from 'lucide-react';
import { isValidationError, isApiError, isNetworkError } from '@/lib/errors';
import type { ApiError } from '@/lib/errors';
import * as Sentry from "@sentry/react";
import { CopyableTraceId } from '@/components/ui/CopyableTraceId';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
  fullScreen?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorType: 'validation' | 'api' | 'network' | 'unknown';
}

const isDevelopment = import.meta.env.DEV;

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    errorType: 'unknown',
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    let errorType: State['errorType'] = 'unknown';
    
    if (isValidationError(error)) {
      errorType = 'validation';
    } else if (isApiError(error)) {
      errorType = 'api';
    } else if (isNetworkError(error)) {
      errorType = 'network';
    }

    return {
      hasError: true,
      error,
      errorInfo: null,
      errorType,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    let errorType: State['errorType'] = 'unknown';
    
    if (isValidationError(error)) {
      errorType = 'validation';
    } else if (isApiError(error)) {
      errorType = 'api';
    } else if (isNetworkError(error)) {
      errorType = 'network';
    }

    this.setState({
      error,
      errorInfo,
      errorType,
    });

    this.props.onError?.(error, errorInfo);

    Sentry.captureException(error, {
      extra: {
        errorInfo,
        errorType,
      },
      tags: {
        errorType,
      },
    });

    if (isDevelopment) {
      console.error('ErrorBoundary caught an error:', {
        type: errorType,
        error,
        errorInfo,
      });
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  private getErrorContent() {
    const { error, errorType } = this.state;

    switch (errorType) {
      case 'validation':
        return {
          icon: AlertCircle,
          iconBg: 'bg-warning-50',
          iconColor: 'text-warning-600',
          title: 'Data Validation Error',
          message: 'The data received from the server is not in the expected format. This might be a temporary issue. Please try again.',
        };
      
      case 'api': {
        const apiError = error as ApiError;
        return {
          icon: AlertTriangle,
          iconBg: 'bg-danger-50',
          iconColor: 'text-danger-600',
          title: apiError.status === 404 ? 'Resource Not Found' : 'API Error',
          message: apiError.message || 'An error occurred while communicating with the server. Please try again later.',
        };
      }
      
      case 'network':
        return {
          icon: Wifi,
          iconBg: 'bg-info-50',
          iconColor: 'text-info-600',
          title: 'Network Connection Issue',
          message: 'Unable to connect to the server. Please check your internet connection and try again.',
        };
      
      default:
        return {
          icon: AlertTriangle,
          iconBg: 'bg-danger-50',
          iconColor: 'text-danger-600',
          title: 'Unexpected Application Error',
          message: 'Something went wrong while processing your request. Our team has been notified and is working to fix this issue.',
        };
    }
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo } = this.state;
      const showDetails = this.props.showDetails ?? isDevelopment;
      const wrapperClass = (this.props.fullScreen ?? true)
        ? 'fixed inset-0 flex items-center justify-center p-6 overflow-auto bg-background'
        : 'flex items-center justify-center min-h-full p-6 overflow-auto';

      const errorContent = this.getErrorContent();
      const Icon = errorContent.icon;

      return (
        <div className={wrapperClass}>
          <div className="max-w-lg w-full">
            <div className="bg-card border border-border-default rounded-xl p-8 text-center shadow-md">
              <div className="flex justify-center mb-6">
                <div className={`w-16 h-16 ${errorContent.iconBg} rounded-full flex items-center justify-center`}>
                  <Icon className={`w-8 h-8 ${errorContent.iconColor}`} />
                </div>
              </div>

              <h1 className="text-2xl font-bold text-neutral-950 mb-3">
                {errorContent.title}
              </h1>

              <p className="text-neutral-600 mb-6 leading-relaxed">
                {errorContent.message}
              </p>

              {isApiError(error) && error.traceId && (
                <div className="mb-6 text-left">
                  <CopyableTraceId traceId={error.traceId} />
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <button
                  onClick={this.handleReset}
                  className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="inline-flex items-center px-4 py-2 bg-surface-1 text-neutral-950 border border-border-default rounded-lg hover:bg-surface-0 transition-colors duration-200 font-medium"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Go Home
                </button>

                <button
                  onClick={this.handleReload}
                  className="inline-flex items-center px-4 py-2 bg-surface-1 text-neutral-600 border border-border-default rounded-lg hover:bg-surface-0 transition-colors duration-200 font-medium"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reload Page
                </button>
              </div>

              {showDetails && error && (
                <details className="mt-6 text-left">
                  <summary className="cursor-pointer text-sm font-medium text-text-secondary hover:text-neutral-950 transition-colors duration-200 flex items-center">
                    <Bug className="w-4 h-4 mr-2" />
                    Error Details (Development)
                  </summary>

                  <div className="mt-3 p-4 bg-surface-1 rounded-lg border border-border-subtle">
                    <div className="space-y-3">
                      <div>
                        <h4 className="text-sm font-semibold text-neutral-950 mb-1">Error Message:</h4>
                        <p className="text-xs text-danger-600 font-mono bg-danger-50 p-2 rounded border border-tints-danger-12">
                          {error.message}
                        </p>
                      </div>

                      {error.stack && (
                        <div>
                          <h4 className="text-sm font-semibold text-neutral-950 mb-1">Stack Trace:</h4>
                          <pre className="text-xs text-neutral-600 font-mono bg-surface-0 p-3 rounded border border-border-subtle overflow-auto max-h-40">
                            {error.stack}
                          </pre>
                        </div>
                      )}

                      {errorInfo?.componentStack && (
                        <div>
                          <h4 className="text-sm font-semibold text-neutral-950 mb-1">Component Stack:</h4>
                          <pre className="text-xs text-neutral-600 font-mono bg-surface-0 p-3 rounded border border-border-subtle overflow-auto max-h-32">
                            {errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                </details>
              )}

              <div className="mt-6 pt-6 border-t border-border-subtle">
                <p className="text-xs text-neutral-600">
                  If this problem persists, please contact our support team with the error details above.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
